/**
 * Parse a workflow-audit-log summary string (generated server-side by
 * the SECURITY DEFINER trigger in `workflow_audit_log` migration) and
 * render it localized. The DB emits English fragments because the
 * trigger runs without user-language context; localization happens at
 * display time.
 *
 * Recognised formats:
 *   • `Created: <name>`               — migration 20260420130000
 *   • `Deleted: <name>`
 *   • `Renamed: <old> → <new>`
 *   • `Co-op enabled` / `Co-op disabled`
 *   • `Edited <hashBefore> → <hashAfter>`
 *   • `Invited user <uuid> as <role>` — migration 20260421200000
 *   • `Removed user <uuid> (was <role>)`
 *   • `Role change for user <uuid>: <old> → <new>`
 *   • `User <uuid> accepted invite`
 *
 * Unknown formats fall through to the raw string.
 */
export function formatAuditSummary(
  summary: string | null | undefined,
  t: (key: string) => string,
): string {
  if (!summary) return '';
  const raw = summary.trim();

  let m = raw.match(/^Created:\s*(.*)$/);
  if (m) return `${t('auditCreated')}: ${m[1]}`;

  m = raw.match(/^Deleted:\s*(.*)$/);
  if (m) return `${t('auditDeleted')}: ${m[1]}`;

  m = raw.match(/^Renamed:\s*(.*?)\s*→\s*(.*)$/);
  if (m) return `${t('auditRenamed')}: ${m[1]} → ${m[2]}`;

  if (raw === 'Co-op enabled') return t('auditCoopEnabled');
  if (raw === 'Co-op disabled') return t('auditCoopDisabled');

  m = raw.match(/^Edited\s+([a-f0-9]+)\s*→\s*([a-f0-9]+)$/);
  if (m) return `${t('auditEdited')} ${m[1]} → ${m[2]}`;

  // Collaborator-events — migration 20260421200000. Identiteterna
  // (user-UUIDs) får bubbla genom oförändrade; UI kan slå upp emails
  // via samma `actorEmails`-map som för andra rader om så önskas.
  m = raw.match(/^Invited user (\S+) as (\w+)$/);
  if (m) return `${t('auditCollabInvite')} ${m[1]} (${m[2]})`;

  m = raw.match(/^Removed user (\S+) \(was (\w+)\)$/);
  if (m) return `${t('auditCollabRemove')} ${m[1]} (${m[2]})`;

  m = raw.match(/^Role change for user (\S+): (\w+) → (\w+)$/);
  if (m) return `${t('auditCollabRoleChange')} ${m[1]}: ${m[2]} → ${m[3]}`;

  m = raw.match(/^User (\S+) accepted invite$/);
  if (m) return `${t('auditCollabAccept')} ${m[1]}`;

  return raw;
}
